/**
 * Room screens — shared cloud browser (Hyperbeam) or synced direct video on a
 * world's screen props, the same for everyone in the room, desktop and VR.
 *
 * Source of truth: the `room-screen` edge function (table room_screens).
 * Live updates: the world's Realtime channel —
 *   screen:changed  {screenId, screen?}  someone shared / stopped; refetch
 *                                        (video screens apply directly)
 *   screen:video    {screenId, video}    play / pause / seek of a direct video
 *   screen:sync:request                  late joiner asks peers for video state
 *                                        when the function is unreachable
 * Embed URLs are only ever taken from the function, never from a peer's
 * broadcast, so nobody can point the room at a session of their choosing.
 *
 * Cost: starting a cloud browser is a Creator Council perk (enforced by the
 * function). Viewers watch free, but every connected viewer is billed by
 * Hyperbeam per minute, so each viewer's client sends a heartbeat ("beat")
 * about once a minute while connected; the function meters those minutes to
 * the sharer and ends sessions past their allowance or the global cap.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { supabase } from '../config/supabase';
import { getChannel, onChannelSubscribed } from './realtimeManager';
import { useWorldStore } from '../stores/worldStore';
import { normalizePanelUrl } from '../utils/panelUrl';
import { isDirectVideoUrl, toCloudBrowserUrl } from '../utils/roomScreenUrl';
import { videoTimeAt, type VideoSyncState } from '../utils/roomScreenSync';
import type { HyperbeamEmbed } from '@hyperbeam/web';

export interface RoomScreenState {
  screenId: string;
  mode: 'browser' | 'video';
  url: string;
  embedUrl: string | null;
  sessionId: string | null;
  startedBy: string | null;
  startedByName: string | null;
  video: VideoSyncState | null;
  updatedAt: number;
}

interface Store {
  worldId: string | null;
  /** null until the first answer from the function; false = no HYPERBEAM_API_KEY. */
  configured: boolean | null;
  /** server clock − local clock, ms */
  clockOffset: number;
  screens: Record<string, RoomScreenState>;
  busy: Record<string, boolean>;
  notice: Record<string, string>;
  /** VR share panel open (menu button) */
  sharePanelOpen: boolean;
  /** Desktop share dialog target */
  desktopShareTarget: string | null;
  /** May I start a cloud browser (Council/admin) + minutes left; null until known. */
  access: ScreenAccess | null;
  /** screenId → signed-in viewers credited in the last ~2 min (from the function). */
  watching: Record<string, number>;
}

export interface ScreenAccess {
  canStart: boolean;
  code: 'council_only' | 'allowance_used' | 'paused' | null;
  message: string | null;
  admin: boolean;
  tier: string | null;
  limitMinutes: number | null;
  usedMinutes: number;
  remainingMinutes: number | null;
  councilUrl: string;
}

/** Shown when a non-member clicks Share link (the function is the real gate). */
export const COUNCIL_PERK_NOTICE = 'Sharing a link to the screen is a Creator Council perk. Tip: open the video in a browser tab and use Share my screen — everyone sees it, including headsets.';
export const COUNCIL_PATH = '/council';

export const useRoomScreenStore = create<Store>(() => ({
  worldId: null, configured: null, clockOffset: 0, screens: {}, busy: {}, notice: {},
  sharePanelOpen: false, desktopShareTarget: null, access: null, watching: {},
}));

const FUNCTION = 'room-screen';
const set = useRoomScreenStore.setState;
const get = useRoomScreenStore.getState;

export const serverNow = () => Date.now() + get().clockOffset;

const noticeTimers = new Map<string, ReturnType<typeof setTimeout>>();
function setNotice(screenId: string, text: string, clearAfterMs = 0) {
  const t = noticeTimers.get(screenId);
  if (t) { clearTimeout(t); noticeTimers.delete(screenId); }
  set((s) => ({ notice: { ...s.notice, [screenId]: text } }));
  if (text && clearAfterMs) {
    noticeTimers.set(screenId, setTimeout(() => {
      noticeTimers.delete(screenId);
      if (get().notice[screenId] === text) set((s) => ({ notice: { ...s.notice, [screenId]: '' } }));
    }, clearAfterMs));
  }
}

async function call(body: Record<string, unknown>): Promise<{ data: any; error: string | null; code?: string }> {
  const t0 = Date.now();
  const { data, error } = await supabase.functions.invoke(FUNCTION, { body });
  if (error) {
    // FunctionsHttpError keeps the JSON body on .context (a Response)
    let parsed: any = null;
    try { parsed = await (error as any).context?.json?.(); } catch { /* not JSON */ }
    return { data: parsed, error: parsed?.error || error.message || 'Request failed', code: parsed?.code };
  }
  if (typeof data?.now === 'number') {
    const t1 = Date.now();
    set({ clockOffset: data.now - (t0 + t1) / 2 });
  }
  return { data, error: null };
}

function applyScreen(screenId: string, screen: RoomScreenState | null) {
  set((s) => {
    const screens = { ...s.screens };
    if (screen) screens[screenId] = screen; else delete screens[screenId];
    return { screens };
  });
}

function broadcast(event: string, payload: Record<string, unknown>) {
  const worldId = get().worldId;
  if (!worldId) return;
  try { getChannel(worldId).send({ type: 'broadcast', event, payload }); } catch { /* not joined yet */ }
}

/** Load every screen's state for the world (late joiners land here). */
export async function refreshRoomScreens(): Promise<boolean> {
  const worldId = get().worldId;
  if (!worldId) return false;
  const { data, error } = await call({ action: 'get', worldId });
  if (error || !data) return false;
  const screens: Record<string, RoomScreenState> = {};
  for (const sc of (data.screens || []) as RoomScreenState[]) screens[sc.screenId] = sc;
  set({ configured: !!data.configured, screens });
  return true;
}

/** Wire one world's screens to its Realtime channel. Returns cleanup. */
export function initRoomScreens(worldId: string): () => void {
  set({ worldId, screens: {}, configured: null, notice: {}, busy: {}, watching: {} });
  let live = true;
  const channel = getChannel(worldId);
  channel.on('broadcast', { event: 'screen:changed' }, (msg: any) => {
    const p = msg?.payload;
    if (!live || !p?.screenId) return;
    // A session ended for cost reasons (allowance / monthly cap): show why.
    if (p.screen === null && typeof p.notice === 'string' && p.notice) setNotice(p.screenId, p.notice.slice(0, 240), 60_000);
    if (p.screen?.mode === 'video' || p.screen === null) applyScreen(p.screenId, p.screen);
    void refreshRoomScreens();
  });
  channel.on('broadcast', { event: 'screen:video' }, (msg: any) => {
    const p = msg?.payload;
    if (!live || !p?.screenId || !p.video) return;
    const cur = get().screens[p.screenId];
    if (cur?.mode === 'video') applyScreen(p.screenId, { ...cur, video: p.video });
  });
  channel.on('broadcast', { event: 'screen:sync:request' }, () => {
    if (!live) return;
    for (const sc of Object.values(get().screens)) {
      if (sc.mode === 'video') broadcast('screen:changed', { screenId: sc.screenId, screen: sc });
    }
  });
  // A session Hyperbeam ended (timeout) — the function clears it on read.
  const onEnded = () => { if (live) setTimeout(() => void refreshRoomScreens(), 1500); };
  window.addEventListener('room-screen-ended', onEnded);
  // Know up front whether Share link is a perk this user has.
  void fetchScreenAccess(true);
  const offUser = useWorldStore.subscribe((s, prev) => { if (live && s.user?.id !== prev.user?.id) void fetchScreenAccess(true); });
  const off = onChannelSubscribed(worldId, (status) => {
    if (status !== 'SUBSCRIBED' || !live) return;
    void refreshRoomScreens().then((ok) => { if (!ok && live) broadcast('screen:sync:request', {}); });
  });
  return () => { live = false; off(); offUser(); window.removeEventListener('room-screen-ended', onEnded); set({ worldId: null, screens: {} }); };
}

// ---- Council access (cached) ----
let accessAt = 0;
let accessInflight: Promise<ScreenAccess | null> | null = null;

/** Ask the function whether I may start a cloud browser and how many minutes are left (cached 60 s). */
export async function fetchScreenAccess(force = false): Promise<ScreenAccess | null> {
  const worldId = get().worldId;
  if (!useWorldStore.getState().user || !worldId) { set({ access: null }); return null; }
  if (!force && get().access && Date.now() - accessAt < 60_000) return get().access;
  if (accessInflight) return accessInflight;
  accessInflight = (async () => {
    const { data, error } = await call({ action: 'access', worldId });
    if (error || !data?.access) return get().access;
    accessAt = Date.now();
    set({ access: data.access as ScreenAccess, ...(data.watching && typeof data.watching === 'object' ? { watching: data.watching } : {}) });
    return data.access as ScreenAccess;
  })().finally(() => { accessInflight = null; });
  return accessInflight;
}

/** Council member or admin: Share link is available (greyed out otherwise). */
export function isScreenLinkMember(access: ScreenAccess | null): boolean {
  return !!access && (access.admin || !!access.tier);
}

export function useScreenLinkMember(): boolean {
  return useRoomScreenStore((s) => isScreenLinkMember(s.access));
}

/**
 * Share link shows greyed out: guests, and signed-in non-members once their
 * access is known (a failed check leaves it enabled; the function still gates).
 */
export function useScreenLinkLocked(): boolean {
  const signedIn = useWorldStore((s) => !!s.user);
  const access = useRoomScreenStore((s) => s.access);
  return !signedIn || (!!access && !isScreenLinkMember(access));
}

/**
 * The sharer's live "Watching: N" for a cloud browser they started: signed-in
 * viewers the function credited in the last ~2 min (guests aren't counted or
 * charged). Polls the access action every 30 s while `enabled`. null = unknown.
 */
export function useWatchingCount(screenId: string | null, enabled: boolean): number | null {
  const count = useRoomScreenStore((s) => (screenId && screenId in s.watching ? s.watching[screenId] : null));
  useEffect(() => {
    if (!enabled || !screenId) return;
    void fetchScreenAccess(true);
    const t = setInterval(() => void fetchScreenAccess(true), 30_000);
    return () => clearInterval(t);
  }, [enabled, screenId]);
  return enabled ? count : null;
}

/** "1,234 of 1,500 cloud browser minutes left this month" for the share UI. */
export function describeAllowance(access: ScreenAccess | null): string {
  if (!access || !isScreenLinkMember(access)) return '';
  if (access.code === 'paused') return access.message || '';
  if (access.admin) return 'Admin: no per-person limit on cloud browser minutes.';
  if (access.limitMinutes == null) return '';
  const n = (v: number) => v.toLocaleString('en-US');
  return `${n(access.remainingMinutes ?? 0)} of ${n(access.limitMinutes)} cloud browser minutes left this month (every viewer's minute counts).`;
}

// ---- Viewer heartbeat (metering) ----
// The function credits the signed-in ACCOUNT from the auth token (one per
// session, however many tabs; guests aren't charged). viewerId is local
// bookkeeping only and is still sent so older function versions accept it.
const BEAT_MS = 60_000;
const viewerId = (() => {
  try { return crypto.randomUUID(); } catch { return `v${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; }
})();
const connected = new Map<string, string>(); // screenId → sessionId
let beatTimer: ReturnType<typeof setInterval> | null = null;

async function sendBeat(screens: { screenId: string; sessionId: string }[], leaving = false) {
  const worldId = get().worldId;
  if (!worldId || !screens.length) return;
  const { data, error } = await call({ action: 'beat', worldId, viewer: viewerId, screens, leaving });
  if (error || !data) return;
  const counts = (data.screens || []).filter((r: any) => typeof r?.watching === 'number');
  if (counts.length) set((s) => ({ watching: { ...s.watching, ...Object.fromEntries(counts.map((r: any) => [r.screenId, r.watching])) } }));
  for (const e of (data.ended || []) as { screenId: string; message: string }[]) {
    applyScreen(e.screenId, null);
    connected.delete(e.screenId);
    setNotice(e.screenId, e.message, 60_000);
    broadcast('screen:changed', { screenId: e.screenId, screen: null, notice: e.message });
  }
  if (data.ended?.length) void fetchScreenAccess(true);
}

/**
 * useHyperbeamScreen reports when this viewer starts / stops receiving a
 * screen's cloud browser. Connected screens are metered once a minute.
 */
export function reportHyperbeamConnection(screenId: string, sessionId: string | null) {
  const prev = connected.get(screenId);
  if (sessionId) {
    if (prev === sessionId) return;
    connected.set(screenId, sessionId);
    void sendBeat([{ screenId, sessionId }]);
  } else {
    if (!prev) return;
    connected.delete(screenId);
    void sendBeat([{ screenId, sessionId: prev }], true);
  }
  if (connected.size && !beatTimer) {
    beatTimer = setInterval(() => void sendBeat([...connected].map(([screenId, sessionId]) => ({ screenId, sessionId }))), BEAT_MS);
  } else if (!connected.size && beatTimer) {
    clearInterval(beatTimer); beatTimer = null;
  }
}

// ---- Hyperbeam clients by screen (filled by useHyperbeamScreen) ----
export const hyperbeamClients = new Map<string, HyperbeamEmbed>();

/**
 * VR typing into the cloud browser: the 3D keyboard sends key presses here
 * (there is no DOM focus in VR, and the headset's own keyboard can't reach
 * the hidden embed). Returns false when the screen isn't connected.
 */
export function typeToScreen(screenId: string, input: { text: string } | { key: 'Backspace' | 'Enter' | 'Tab' }): boolean {
  const hb = hyperbeamClients.get(screenId);
  if (!hb) return false;
  const press = (key: string) => {
    hb.sendEvent({ type: 'keydown', key, ctrlKey: false, metaKey: false });
    hb.sendEvent({ type: 'keyup', key, ctrlKey: false, metaKey: false });
  };
  if ('key' in input) press(input.key);
  // Text goes in through the embed's text channel (what its own IME / paste
  // path uses), so any character and whole pasted links arrive in one go.
  else if (input.text) hb.sendEvent({ type: 'text', text: input.text } as unknown as Parameters<HyperbeamEmbed['sendEvent']>[0]);
  return true;
}

async function waitForClient(screenId: string, ms = 12000): Promise<HyperbeamEmbed | null> {
  const end = Date.now() + ms;
  while (Date.now() < end) {
    const hb = hyperbeamClients.get(screenId);
    if (hb) return hb;
    await new Promise((r) => setTimeout(r, 250));
  }
  return null;
}

/**
 * Client-side mirror of the function's SHARE_POLICY ('signed-in'). To restrict
 * sharing later, change both: here (hides the controls) and in
 * supabase/functions/room-screen/logic.ts (enforces it).
 */
export function canShareToScreens(): boolean {
  return !!useWorldStore.getState().user;
}

/** The world owner and the sharer can always stop; under 'signed-in', anyone signed in. */
export function canStopScreen(screen: RoomScreenState, isOwner: boolean, myId: string | null): boolean {
  return isOwner || (!!myId && screen.startedBy === myId) || canShareToScreens();
}

/** A share failure with the function's code (council_only, allowance_used, paused…). */
export class ScreenShareError extends Error {
  constructor(message: string, public code?: string) { super(message); }
}

/**
 * Put a link on a screen for everyone. Web pages/YouTube → cloud browser;
 * .mp4/.webm/.m3u8 → synced video. Throws a user-facing message on failure.
 */
export async function shareToScreen(screenId: string, input: string, size?: { width?: number; height?: number }): Promise<void> {
  const worldId = get().worldId;
  if (!worldId) throw new Error('Not in a world');
  if (!canShareToScreens()) throw new Error('Sign in to share to the screen');
  const normalized = normalizePanelUrl(input);
  const url = isDirectVideoUrl(normalized) ? normalized : toCloudBrowserUrl(normalized);
  set((s) => ({ busy: { ...s.busy, [screenId]: true } }));
  setNotice(screenId, isDirectVideoUrl(url) ? 'Starting video…' : 'Starting the cloud browser…');
  try {
    const name = useWorldStore.getState().userName || undefined;
    let res = await call({ action: 'share', worldId, screenId, url, width: size?.width, height: size?.height, name });
    if (res.error) {
      if (res.code === 'not_configured') set({ configured: false });
      if (res.data?.access) { accessAt = Date.now(); set({ access: res.data.access }); }
      throw new ScreenShareError(res.code === 'not_configured'
        ? "Cloud browser isn't set up yet. Video file links (.mp4, .webm, .m3u8) still work."
        : res.error, res.code);
    }
    applyScreen(screenId, res.data.screen);
    broadcast('screen:changed', { screenId, screen: res.data.screen });
    if (res.data.reused && res.data.adminToken) {
      // Same session, new page: navigate it in place so nobody reconnects.
      const hb = await waitForClient(screenId);
      let ok = false;
      if (hb) {
        try {
          hb.adminToken = res.data.adminToken;
          await hb.tabs.update({ url } as any);
          ok = true;
        } catch (e) { console.warn('[RoomScreen] navigate failed, restarting session', e); }
      }
      if (!ok) {
        res = await call({ action: 'share', worldId, screenId, url, width: size?.width, height: size?.height, name, fresh: true });
        if (res.error) throw new ScreenShareError(res.error, res.code);
        applyScreen(screenId, res.data.screen);
        broadcast('screen:changed', { screenId, screen: res.data.screen });
      }
    }
    setNotice(screenId, '');
    if (!isDirectVideoUrl(url)) void fetchScreenAccess(true);
  } catch (e) {
    setNotice(screenId, e instanceof Error ? e.message : 'Could not share');
    throw e;
  } finally {
    set((s) => ({ busy: { ...s.busy, [screenId]: false } }));
  }
}

export async function stopScreen(screenId: string): Promise<void> {
  const worldId = get().worldId;
  if (!worldId) return;
  const prev = get().screens[screenId];
  applyScreen(screenId, null);
  const res = await call({ action: 'stop', worldId, screenId });
  if (res.error) {
    if (prev) applyScreen(screenId, prev);
    setNotice(screenId, res.error);
    throw new Error(res.error);
  }
  broadcast('screen:changed', { screenId, screen: null });
}

/** Play / pause / seek a direct video for everyone. */
export function controlVideo(screenId: string, change: { playing?: boolean; seekBy?: number; t?: number }) {
  const cur = get().screens[screenId];
  if (cur?.mode !== 'video' || !cur.video || !canShareToScreens()) return;
  const now = serverNow();
  const t = Math.max(0, change.t ?? videoTimeAt(cur.video, now) + (change.seekBy ?? 0));
  const video: VideoSyncState = { playing: change.playing ?? cur.video.playing, t, at: now };
  applyScreen(screenId, { ...cur, video });
  broadcast('screen:video', { screenId, video });
  const worldId = get().worldId;
  if (worldId) void call({ action: 'video', worldId, screenId, video: { playing: video.playing, t } });
}

export function openSharePanel(open: boolean) { set({ sharePanelOpen: open }); }
export function openDesktopShare(screenId: string | null) { set({ desktopShareTarget: screenId }); }
